/** Canvas → Workspace exit transition — pure state machine
 * (VISTOERA_WORKSPACE_CANVAS_BIDIRECTIONAL_SPATIAL_SHELL_TRANSITION_AND_VIEWPORT_CONTINUITY_P0_V1
 * §11/§66/§68/§69).
 *
 * Deliberately framework-agnostic (no React import) so the transition
 * state machine — the part with actual timing/guard logic worth
 * regression-testing — can be exercised head-less via
 * `scripts/canvasWorkspaceTransition.node-test.ts` without mounting any
 * component tree. `CanvasWorkspaceTransitionProvider`
 * (`canvasWorkspaceTransition.tsx`) is a thin React subscription wrapper
 * around exactly one instance of this controller.
 *
 * Contract:
 * - `requestExit` is the ONLY way the `exiting` phase is entered. A
 *   second call while already pending (rapid V-logo clicks — spec §66
 *   "V V V V → NAVIGATION_COUNT=1") is a no-op: at most one `onNavigate`
 *   fires per exit gesture.
 * - `durationMs <= 0` (reduced motion — spec §62) skips the visual phase
 *   entirely and calls `onNavigate` synchronously, so the caller never
 *   has to special-case reduced motion at the call site.
 * - After `durationMs`, `onNavigate` fires but `phase` deliberately does
 *   NOT snap back to `idle` in the same tick. `onNavigate`'s real
 *   `navigate()` and this controller's `phase` are two independent
 *   state sources (router history vs. plain JS here); resetting `phase`
 *   synchronously right after calling `onNavigate` was observed (Preview
 *   E2E — sampled `getBoundingClientRect()` every frame) to render ONE
 *   extra frame of the Canvas Surface snapping back to `translateX(0)`
 *   BEFORE the route change actually unmounts it, because the router's
 *   location-state commit lags the plain `setState` commit by a tick.
 *   Instead the Surface simply stays visually `exiting` (translated
 *   off-screen) until the route change unmounts it for real — no
 *   dangling class, no snap-back. `recoveryGraceMs` after that is a
 *   SAFETY NET ONLY: if the Surface is somehow still mounted that much
 *   later (spec §69 "Route Failure" — destination route never actually
 *   loaded), `phase` resets to `idle` so the V logo recovers instead of
 *   staying stuck disabled/off-screen forever.
 */

export type CanvasTransitionPhase = 'idle' | 'exiting'

export interface CanvasExitTransitionController {
  getPhase(): CanvasTransitionPhase
  /** Notified whenever `getPhase()` changes. Returns an unsubscribe fn. */
  subscribe(listener: () => void): () => void
  /** Canonical V-logo exit trigger. `onNavigate` performs the actual
   * route handoff and is invoked exactly once per accepted request. */
  requestExit(onNavigate: () => void): void
  dispose(): void
}

/** Spec §69 recovery-only safety net — see the file docstring above.
 * Generous relative to a real same-SPA route swap (which commits within
 * a frame or two) so it never fires in the normal, successful path. */
export const CANVAS_EXIT_RECOVERY_GRACE_MS = 500

export function createCanvasExitTransitionController(
  durationMs: number,
  recoveryGraceMs: number = CANVAS_EXIT_RECOVERY_GRACE_MS,
): CanvasExitTransitionController {
  let phase: CanvasTransitionPhase = 'idle'
  let pending = false
  let exitTimer: ReturnType<typeof setTimeout> | null = null
  let recoveryTimer: ReturnType<typeof setTimeout> | null = null
  const listeners = new Set<() => void>()

  const emit = () => {
    listeners.forEach((listener) => listener())
  }

  const setPhase = (next: CanvasTransitionPhase) => {
    if (phase === next) return
    phase = next
    emit()
  }

  const clearTimers = () => {
    if (exitTimer != null) {
      clearTimeout(exitTimer)
      exitTimer = null
    }
    if (recoveryTimer != null) {
      clearTimeout(recoveryTimer)
      recoveryTimer = null
    }
  }

  return {
    getPhase: () => phase,
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
    requestExit: (onNavigate) => {
      if (pending) return
      pending = true
      if (durationMs <= 0) {
        // Reduced motion / instant — no visual phase, single navigation.
        pending = false
        onNavigate()
        return
      }
      setPhase('exiting')
      clearTimers()
      exitTimer = setTimeout(() => {
        exitTimer = null
        onNavigate()
        pending = false
        // Do NOT setPhase('idle') here — see file docstring. Only a
        // recovery safety net if the Surface is still around later.
        recoveryTimer = setTimeout(() => {
          recoveryTimer = null
          setPhase('idle')
        }, recoveryGraceMs)
      }, durationMs)
    },
    dispose: () => {
      clearTimers()
      pending = false
      listeners.clear()
    },
  }
}
